import React, { useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useNavigate } from "react-router-dom";
import Lenis from "lenis";
import { useLang } from "@/i18n";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";

const PORTRAIT =
  "https://customer-assets-v7afamib.emergentagent.net/job_design-motion-48/artifacts/3h3txzyj_YSF08349-1-WIDE.webp";

// Phrases highlighted in the bio (same as home About)
const HIGHLIGHTS = [
  "After Effects", "Premiere Pro",
  "motion design",
  "video editing",
  "brand storytelling",
  "AI-supported tools",
];
const HL_RE = new RegExp(`(${HIGHLIGHTS.map((p) => p.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`, "gi");
const highlight = (text) =>
  text.split(HL_RE).map((part, i) =>
    HIGHLIGHTS.some((p) => p.toLowerCase() === part.toLowerCase()) ? (
      <span key={i} className="text-navy-ink font-normal">{part}</span>
    ) : (
      part
    )
  );

export default function AboutPage() {
  const { t } = useLang();
  const navigate = useNavigate();
  const { scrollY } = useScroll();
  const imgScale = useTransform(scrollY, [0, 400], [1, 1.08]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let raf;
    const loop = (time) => { lenis.raf(time); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);

  const handleNav = (key) => {
    if (key === "top") return navigate("/");
    navigate(`/#${key}`);
  };

  return (
    <div className="relative bg-void min-h-screen">
      <div className="noise-overlay" />
      <Navbar onNavigate={handleNav} />

      <main>
        {/* Hero: full-bleed color portrait */}
        <section className="relative min-h-screen w-full overflow-hidden pt-24">
          <motion.div style={{ scale: imgScale }} className="absolute inset-0">
            <img
              src={PORTRAIT}
              alt="Polat Pala"
              data-testid="about-page-portrait"
              className="h-full w-full object-cover"
              style={{ objectPosition: "center 20%" }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-void via-void/30 to-void/60" />
            <div className="absolute inset-0 bg-gradient-to-r from-void/50 via-transparent to-void/50" />
          </motion.div>

          <div className="relative z-10 mx-auto max-w-[1600px] px-6 md:px-12 pt-24 md:pt-32">
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="font-display text-xl md:text-2xl uppercase text-navy-ink"
            >
              About
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              className="mt-4 font-display text-6xl sm:text-8xl md:text-[10rem] font-bold uppercase tighter leading-[0.85] text-bone"
            >
              Polat<br />Pala
            </motion.h1>
          </div>

          <div className="absolute bottom-10 left-0 right-0 z-10 mx-auto max-w-[1600px] px-6 md:px-12 flex items-end justify-between">
            <span className="font-display text-xl md:text-2xl uppercase text-bone/50">
              Video Editor <span className="text-navy-ink">/</span> Motion Designer
            </span>
          </div>
        </section>

        {/* Full bio */}
        <section className="relative border-t border-border bg-void">
          <div className="mx-auto max-w-[1600px] px-6 md:px-12 py-28 md:py-40">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
              <div className="lg:col-span-4">
                <h2 className="font-display text-4xl md:text-6xl font-bold uppercase tighter leading-[0.9] text-bone">
                  The<br />Story
                </h2>
              </div>

              <div className="lg:col-span-8 space-y-8 max-w-2xl">
                {t.about.longBio.map((para, i) => (
                  <motion.p
                    key={i}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.7, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                    className="font-body font-light text-lg md:text-xl text-bone/75 leading-relaxed"
                  >
                    {highlight(para)}
                  </motion.p>
                ))}

                <motion.div
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.7, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
                  className="pt-6 border-t border-border space-y-4"
                >
                  <p
                    data-testid="about-page-reach-line"
                    className="font-body font-light text-lg md:text-xl text-navy-ink leading-relaxed"
                  >
                    {t.about.reachLine}
                  </p>
                  <a
                    href="mailto:hello@polatpala.com"
                    data-testid="about-page-email-link"
                    className="inline-block font-display text-xl sm:text-2xl md:text-3xl lg:text-4xl font-medium tracking-tight text-bone hover:text-navy-ink transition-colors duration-300 break-all"
                  >
                    hello@polatpala.com
                  </a>
                </motion.div>
              </div>
            </div>
          </div>
        </section>

        {/* Back to work CTA */}
        <section className="relative border-t border-border bg-void">
          <div className="mx-auto max-w-[1600px] px-6 md:px-12 py-24 md:py-32 text-center">
            <button
              onClick={() => navigate("/#work")}
              data-testid="about-back-to-work"
              className="group inline-flex items-center gap-4 font-display text-4xl md:text-6xl font-bold uppercase tighter leading-[0.9] text-bone hover:text-navy-ink transition-colors duration-300"
            >
              <span>See the work</span>
              <span className="translate-x-0 group-hover:translate-x-4 transition-transform duration-500">→</span>
            </button>
          </div>
        </section>
      </main>

      <Footer onNavigate={handleNav} />
    </div>
  );
}
